<script lang="ts">
	/**
	 * EkaviLockup — mark + EKAVI CARE wordmark.
	 *
	 * Layout strictly mirrors `ekavi-growth/brand/lockup.svg`:
	 *   · mark at 32×32 (visual), wordmark in caps with +120 tracking
	 *   · gap = half mark width
	 *   · monochrome charcoal on bone — never gradient, never two-colour
	 *
	 * Renders as inline-flex so it sits on a page row without
	 * hijacking the parent's baseline; the wordmark uses Söhne caps if
	 * licensed, falls back to Inter caps otherwise (per brand).
	 */

	interface Props {
		height?: number;
		title?: string;
		linkHome?: boolean;
		decorative?: boolean;
	}

	let { height = 32, title = 'Ekavi Care', linkHome = true, decorative = false }: Props = $props();

	const markSize = $derived(height);

	const Tag = $derived(linkHome ? 'a' : 'span');
</script>

<svelte:element
	this={Tag}
	class="lockup"
	href={linkHome ? '/' : undefined}
	aria-label={decorative ? undefined : title}
	role={decorative ? 'presentation' : undefined}
	style:--lockup-height="{height}px"
>
	<span class="mark" aria-hidden="true">
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={markSize}
			height={markSize}
			viewBox="0 0 100 100"
			role="presentation"
			aria-hidden="true"
		>
			<defs>
				<mask id="ekavi-lockup-spark">
					<rect x="0" y="0" width="100" height="100" fill="white" />
					<path
						d="M 68 55 C 68 52.09, 67.44 49.36, 66.32 46.81 C 65.24 44.26, 63.75 42.03, 61.86 40.14 C 59.97 38.25, 57.75 36.76, 55.19 35.68 C 52.63 34.56, 49.90 34, 47 34 C 49.90 34, 52.63 33.46, 55.19 32.37 C 57.75 31.25, 59.97 29.75, 61.86 27.86 C 63.75 25.97, 65.24 23.74, 66.32 21.19 C 67.44 18.64, 68 15.91, 68 13 C 68 15.91, 68.54 18.64, 69.63 21.19 C 70.75 23.74, 72.25 25.97, 74.14 27.86 C 76.03 29.75, 78.25 31.24, 80.81 32.32 C 83.37 33.46, 86.10 34, 89 34 C 86.10 34, 83.37 34.56, 80.81 35.68 C 78.25 36.76, 76.03 38.25, 74.14 40.14 C 72.25 42.03, 70.75 44.26, 69.63 46.81 C 68.54 49.36, 68 52.09, 68 55 Z"
						fill="black"
					/>
				</mask>
			</defs>
			<path
				d="M 50 12 C 78 -18, 145 28, 50 92 C -45 28, 22 -18, 50 12 Z"
				fill="currentColor"
				mask="url(#ekavi-lockup-spark)"
			/>
		</svg>
	</span>
	<span class="wordmark">EKAVI CARE</span>
</svelte:element>

<style>
	.lockup {
		display: inline-flex;
		align-items: center;
		gap: calc(var(--lockup-height, 32px) * 0.5);
		color: var(--ekavi-charcoal);
		text-decoration: none;
		border-bottom: none;
		padding: 0;
		line-height: 1;
	}
	.lockup:hover {
		border-bottom: none;
		color: var(--ekavi-charcoal);
		opacity: 0.78;
	}
	.lockup:focus-visible {
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 4px;
	}

	.mark {
		display: inline-flex;
		line-height: 0;
		flex-shrink: 0;
	}

	.wordmark {
		font-family: var(--ekavi-font-ui);
		font-size: calc(var(--lockup-height, 32px) * 0.46);
		font-weight: var(--ekavi-weight-buch);
		letter-spacing: var(--ekavi-track-caps);
		color: inherit;
		white-space: nowrap;
	}
</style>
